import { SelectionModel } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Subject, of } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { EmploymentConditionsConfirmationService } from 'src/app/data/employment-conditions-confirmation.service';
import { Filter } from 'src/app/models/common/filter';
import { EmploymentConditionsConfirmationGridDto } from 'src/app/models/dtos/employment-conditions-confirmation-grid-dto';
import { IPagedResult } from 'src/app/shared/models/PagedResult';
import { GridTableDataSource } from 'src/app/virtual-scroll/data-source';

export class EmploymentConditionsConfirmationListDataSource extends GridTableDataSource<EmploymentConditionsConfirmationGridDto> {
  isLoading$ = new BehaviorSubject<boolean>(false);
  selection = new SelectionModel<EmploymentConditionsConfirmationGridDto>(true, []);
  private cancelationSubject = new Subject();
  private countSubject = new BehaviorSubject<number>(0);
  private _absoluteDataLength: number;
  private readonly MAX_SELECTION = 100;

  count$ = this.countSubject.asObservable();

  get isLoading() {
    return this.isLoading$.getValue();
  }

  get absoluteDataLength() {
    return this._absoluteDataLength;
  }

  public get isAllSelected() {
    return this.allData.length > 0 && this.selection.selected.length > 0 && this.selection.selected.length === this.allData.length;
  }

  public isSelected(item: EmploymentConditionsConfirmationGridDto): boolean {
    return this.selection.isSelected(item);
  }

  public selectAll(): void {
    const toSelect = this.allData.slice(0, this.MAX_SELECTION);
    this.selection.clear();
    this.selection.select(...toSelect);
  }

  public deselectAll(): void {
    this.selection.clear();
  }

  public get isSelectionLimitReached(): boolean {
    return this.selection.selected.length >= this.MAX_SELECTION;
  }

  constructor(
    private employmentConditionsConfirmationService: EmploymentConditionsConfirmationService,
    { viewport }: { viewport?: CdkVirtualScrollViewport } = {},
  ) {
    super([], { viewport });
  }

  disconnect() {
    this.cancelationSubject.next(undefined);
    this.isLoading$.next(false);
  }

  reset() {
    this.cancelationSubject.next(undefined);
    this.isLoading$.next(false);
    this.deselectAll();
    this.allData = [];
  }

  fetch(page: number, count: number, sortingField: string, sortingDirection: string, filters: Filter[]): Promise<void> {
    return new Promise<void>((resolve) => {
      this.isLoading$.next(true);
      this.employmentConditionsConfirmationService
        .getAll(page, count, sortingField, sortingDirection, filters)
        .pipe(
          takeUntil(this.cancelationSubject),
          catchError(() => of([])),
          finalize(() => this.isLoading$.next(false)),
        )
        .subscribe((response: IPagedResult<EmploymentConditionsConfirmationGridDto>) => {
          this._absoluteDataLength = response.Count;
          const isAllSelected = this.isAllSelected;
          this.merge(response.Results, page, count);
          this.keepSelectionState(isAllSelected);
          this.countSubject.next(response.Count);
          resolve();
        });
    });
  }

  private keepSelectionState(isAllSelected: boolean) {
    const old = [...this.selection.selected];
    if (old.length === 0) {
      return;
    }
    this.selection.clear();
    this.allData?.forEach((a) => {
      const wasSelected = old.some((r) => r.EmploymentConditionsConfirmationId === a.EmploymentConditionsConfirmationId);
      const shouldAutoSelect = isAllSelected && this.selection.selected.length < this.MAX_SELECTION;
      if (wasSelected || shouldAutoSelect) {
        this.selection.select(a);
      }
    });
  }

  private merge(data: EmploymentConditionsConfirmationGridDto[], page: number, count: number) {
    const tmp = this.allData.filter((item, index) => {
      return (
        index < count * (page - 1) ||
        index >= count * page ||
        data.some((incoming) => incoming.EmploymentConditionsConfirmationId === item.EmploymentConditionsConfirmationId)
      );
    });

    data?.map(
      (item) => {
        const index = tmp.findIndex((td) => td.EmploymentConditionsConfirmationId === item.EmploymentConditionsConfirmationId);
        if (index > -1) {
          tmp[index] = item;
        } else {
          const index = count * (page - 1) + data.indexOf(item);

          if (index > -1) {
            tmp.splice(index, 0, item);
          } else {
            tmp.push(item);
          }
        }
      },
      (err: any) => console.log(err),
    );

    this.allData = tmp;
  }
}
